// @flow
import {
    type ItemActionsConfigDTO,
    ItemLinkType,
    SearchItemType,
} from "data/types";

// this file defines all the system pre-defined actionPanel commandActions available
/* istanbul ignore next */
export const getItemActionsConfig = (): ItemActionsConfigDTO => ({
    categories: [
        {
            id: "filter",
            name: "$actionCategory:filter.name",
            icon: "filter",
            shouldBeVisible: [
                "isAnyOnlineStatus",
                "isAnySelection",
                "hasRowAndColId",
            ],
        },
        {
            id: "general",
            name: "$actionCategory:general.name",
            icon: "hand-o-right",
            shouldBeVisible: ["isAnyOnlineStatus", "isAnySelection"],
        },
        {
            id: "doc",
            name: "$actionCategory:doc.name",
            icon: "file-text-o",
            shouldBeVisible: ["isAnyOnlineStatus", "isAnySelection"],
        },
        {
            id: "doclist",
            name: "$actionCategory:doclist.name",
            icon: "files-o",
            shouldBeVisible: ["isAnyOnlineStatus", "isAnySelection"],
        },
        {
            id: "wf",
            name: "$actionCategory:wf.name",
            icon: "check-square-o",
            shouldBeVisible: ["!hasContext:isFlatAU", "isWorkflowEnabled"],
        },
        {
            id: "reminder",
            name: "$actionCategory:reminder.name",
            icon: "clock-o",
        },
        {
            id: "history",
            name: "Activities",
            expanded: true,
            shouldBeVisible: ["hasContext:workflow", "isAnyOnlineStatus"],
        },
        {
            id: "sync",
            name: "$actionCategory:sync.name",
            icon: "external-link",
            shouldBeVisible: ["isSyncEnabled", "isAnySelection"],
        },
        {
            id: "clipboard",
            name: "$actionCategory:clipboard.name",
            icon: "clipboard",
            shouldBeVisible: ["isValidItemUri", "isAnyOnlineStatus"],
        },
        {
            name: "$actionCategory:others.name",
            shouldBeVisible: ["isAnyOnlineStatus"],
        },
        {
            id: "recyclebin",
            name: "$actionCategory:recyclebin",
            icon: "trash",
            shouldBeVisible: ["isAnySelection", "isRecycleBin"],
        },
        {
            id: "offline",
            name: "$actionCategory:offline",
            shouldBeVisible: [
                "hasFeature:offline",
                "isAnySelection",
                "isAnyOnlineStatus",
            ],
        },
    ],
    actions: [
        {
            id: "folder_activities",
            category: "history",
            views: ["home", "wf"],
            shouldBeVisible: ["isFolder", "isValidItemUri"],
            commandAction: {
                name: "folder_preview",
            },
            view: {
                name: "itemView_activityList",
                isDoc: false,
                showAddNote: false,
                showHistory: false,
                showNotes: true,
                showRetentionInfo: false,
            },
        },
        {
            id: "doc_activities",
            category: "history",
            shouldBeVisible: ["isDoc", "isValidItemUri"],
            views: ["home", "wf", "news"],
            commandAction: {
                name: "doc_preview",
            },
            view: {
                name: "itemView_activityList",
                isDoc: true,
                showAddNote: false,
                showHistory: true,
                showNotes: true,
            },
        },
        {
            id: "doclist_exportCsv",
            category: "doclist",
            name: "$itemActions:doclist_exportCsv.name",
            icon: "file-excel-o",
            views: ["flat", "folder", "miniflat"],
            commandAction: { name: "doclist_exportCsv" },
        },
        {
            id: "doclist_filters",
            category: "filter",
            views: ["flat", "folder", "miniflat", "mailbox"],
            view: {
                name: "itemView_filter",
                actions: [
                    "filterByValue",
                    "filterAddValue",
                    "filterRemove",
                    "reset",
                ],
            },
            shouldBeVisible: ["hasRowAndColId"],
            commandAction: {}, //HACK: persist props to itemView
        },
        {
            id: "doc_preview",
            category: "doc",
            name: "$itemActions:doc_preview.name",
            icon: "eye",
            views: [
                "flat",
                "folder",
                "searchresult",
                "news",
                "home",
                "wf",
                "offline_item",
            ],
            shouldBeVisible: ["isDoc", "isAnyOnlineStatus"],
            commandAction: {
                name: "doc_preview",
            },
        },
        {
            id: "doc_popout",
            category: "doc",
            name: "$itemActions:doc_popout.name",
            icon: "external-link",
            views: [
                "flat",
                "folder",
                "searchresult",
                "docmodal",
                "mailbox",
                "news",
                "home",
                "wf",
                "offline_item",
            ],
            shouldBeVisible: ["isDoc", "isAnyOnlineStatus"],
            commandAction: {
                name: "doc_preview",
                props: {
                    newWindow: true,
                },
            },
        },
        {
            id: "doc_previewAndPrint",
            category: "doc",
            name: "$itemActions:doc_print.name",
            icon: "print",
            views: [
                "flat",
                "folder",
                "mailbox",
                "searchresult",
                "home",
                "news",
                "offline_item",
            ],
            shouldBeVisible: [
                "isDoc",
                "isAnyOnlineStatus",
                "hasItemStatus:PDF",
            ],
            commandAction: {
                name: "doc_preview",
                props: {
                    triggerPrint: true,
                },
            },
        },
        {
            id: "doc_print",
            category: "doc",
            name: "$itemActions:doc_print.name",
            icon: "print",
            views: ["doc", "docmodal"],
            shouldBeVisible: [
                "isDoc",
                "isAnyOnlineStatus",
                "hasItemStatus:PDF",
            ],
            commandAction: {
                name: "doc_print",
            },
        },
        {
            id: "doc_lock",
            category: "doc",
            name: "$itemActions:doc_lock.name",
            icon: "lock",
            views: ["flat", "folder", "searchresult", "docmodal", "document"],
            shouldBeVisible: [
                "isDoc",
                "!hasItemStatus:Locked",
                "hasContext:isDocEditAllowed",
                "isAnySelection",
            ],
            commandAction: {
                name: "lock",
            },
        },
        {
            id: "doc_unlock",
            category: "doc",
            name: "$itemActions:doc_unlock.name",
            icon: "unlock",
            views: ["flat", "folder", "searchresult", "docmodal", "document"],
            shouldBeVisible: [
                "isDoc",
                "hasItemStatus:Locked",
                "hasContext:isDocEditAllowed",
                "isAnySelection",
            ],
            commandAction: {
                name: "unlock",
            },
        },
        {
            id: "fav_add",
            category: "general",
            // name: "Add as Favorite",
            name: "$itemActions:fav_add.name",
            icon: "star-o",
            shouldBeVisible: [
                "!isFav",
                "isValidItemUri",
                "isAnyOnlineStatus",
                "isAnySelection",
            ],
            commandAction: {
                name: "favorite",
                props: {
                    isFav: false,
                },
            },
        },
        {
            id: "fav_remove",
            category: "general",
            name: "$itemActions:fav_remove.name",
            // name: "Remove from Favorites",
            icon: "star",
            shouldBeVisible: [
                "isFav",
                "isValidItemUri",
                "isAnyOnlineStatus",
                "isAnySelection",
            ],
            commandAction: {
                name: "favorite",
                props: {
                    isFav: true,
                },
            },
        },
        {
            id: "share_web",
            category: "general",
            name: "$itemActions:share_web.name",
            icon: "share-alt",
            shouldBeVisible: [
                "hasBrowser_webShareApi",
                "isValidItemUri",
                "!hasSiteType:SharedSites",
                "isAnyOnlineStatus",
            ],
            commandAction: {
                name: "share_web",
            },
        },
        {
            id: "share",
            category: "general",
            name: "$itemActions:share.name",
            icon: "share-alt",
            shouldBeVisible: [
                "isValidItemUri",
                "!hasSiteType:SharedSites",
                "isAnySelection",
            ],
            commandAction: {
                name: "share",
            },
        },
        {
            id: "share_edit",
            category: "general",
            name: "$itemActions:share_edit.name",
            icon: "share-alt-square",
            shouldBeVisible: [
                "isValidItemUri",
                "!hasSiteType:SharedSites",
                "hasItemStatus:Share",
            ],
            commandAction: {
                name: "share",
                props: {
                    isEdit: true,
                },
            },
        },
        {
            id: "search_favorite_add",
            category: "general",
            // name: "Add as Favorite",
            name: "$itemActions:fav_add.name",
            icon: "star-o",
            shouldBeVisible: [
                "hasContext:searchRequest",
                `!hasSearchType:${SearchItemType.Favorite}`,
            ],
            commandAction: {
                name: "search_favorite",
            },
        },
        {
            id: "search_favorite_remove",
            category: "general",
            name: "$itemActions:fav_remove.name",
            // name: "Remove from Favorites",
            icon: "star",
            shouldBeVisible: [
                "hasContext:searchRequest",
                `hasSearchType:${SearchItemType.Favorite}`,
            ],
            commandAction: {
                name: "search_favorite",
            },
        },
        {
            id: "search_preview",
            category: "general",
            name: "$itemActions:search_preview.name",
            icon: "eye",
            views: ["home"],
            shouldBeVisible: ["hasContext:searchRequest"],
            commandAction: {
                name: "search_preview",
            },
        },
        {
            id: "search_popout",
            category: "general",
            name: "$itemActions:search_popout.name",
            icon: "external-link",
            views: ["home"],
            shouldBeVisible: ["hasContext:searchRequest"],
            commandAction: {
                name: "search_preview",
                props: {
                    newWindow: true,
                },
            },
        },
        {
            id: "subscribe",
            category: "general",
            name: "$itemActions:subscribe.name",
            icon: "feed",
            views: ["flat", "folder", "searchresult", "docmodal", "document"],
            shouldBeVisible: [
                "!hasContext:isFlatAU",
                "!hasItemStatus:Subscribed",
                "isAnyOnlineStatus",
                "isAnySelection",
            ],
            commandAction: {
                name: "subscribe",
            },
        },
        {
            id: "unsubscribe",
            category: "general",
            name: "$itemActions:unsubscribe.name",
            icon: "feed",
            views: [
                "flat",
                "folder",
                "searchresult",
                "docmodal",
                "news",
                "document",
            ],
            shouldBeVisible: [
                "hasItemStatus:Subscribed",
                "isAnyOnlineStatus",
                "isAnySelection",
            ],
            commandAction: {
                name: "unsubscribe",
            },
        },
        // {
        //     id: "transfer",
        //     category: "general",
        //     name: "$itemActions:transfer.name",
        //     icon: "exchange",
        //     views: ["flat", "folder", "searchresult", "docmodal", "document"],
        //     shouldBeVisible: ["isValidItemUri"],
        //     commandAction: {
        //         name: "transfer",
        //     },
        // },
        {
            id: "move",
            category: "general",
            name: "$itemActions:transfer.move",
            icon: "exchange",
            views: ["folder", "searchresult", "docmodal", "document"],
            shouldBeVisible: [
                "isValidItemUri",
                "!hasContext:isFlatAU",
                "isAnySelection",
            ],
            commandAction: {
                name: "transfer",
                props: {
                    type: "move",
                },
            },
        },
        {
            id: "copy",
            category: "general",
            name: "$itemActions:transfer.copy",
            icon: "files-o",
            views: ["folder", "searchresult", "docmodal", "document"],
            shouldBeVisible: [
                "isValidItemUri",
                "isDoc",
                "!hasContext:isFlatAU",
                "isAnySelection",
            ],
            commandAction: {
                name: "transfer",
                props: {
                    type: "copy",
                },
            },
        },
        {
            id: "link",
            category: "general",
            name: "$itemActions:transfer.link",
            icon: "link",
            views: ["folder", "searchresult", "docmodal", "document"],
            shouldBeVisible: [
                "isValidItemUri",
                "!hasContext:isFlatAU",
                "isAnySelection",
            ],
            commandAction: {
                name: "transfer",
                props: {
                    type: "link",
                },
            },
        },
        {
            id: "reminder_manage",
            category: "reminder",
            shouldBeVisible: ["isValidItemUri"],
            view: {
                name: "itemView_reminder",
            },
            commandAction: {
                name: "reminder_manage",
            },
        },
        {
            id: "reminder_add",
            category: "reminder",
            name: "$itemActions:reminder_add.name",
            icon: "clock-o",
            shouldBeVisible: ["isValidItemUri"],
            commandAction: {
                name: "reminder_manage",
            },
        },
        {
            id: "folder_preview",
            category: "general",
            name: "$itemActions:folder_preview.name",
            icon: "eye",
            views: [
                "searchresult",
                "news",
                "home",
                "document",
                "wf",
                "offline_item",
            ],
            shouldBeVisible: [
                "isValidItemUri",
                "!isMiniView",
                "!hasContext:isFlatAU",
            ],
            commandAction: {
                name: "folder_preview",
            },
        },
        {
            id: "folder_popout",
            category: "general",
            name: "$itemActions:folder_popout.name",
            icon: "external-link",
            views: [
                "searchresult",
                "news",
                "home",
                "document",
                "wf",
                "offline_item",
            ],
            shouldBeVisible: [
                "isValidItemUri",
                "isAnyOnlineStatus",
                "!isMiniView",
                "!hasContext:isFlatAU",
            ],
            commandAction: {
                name: "folder_preview",
                props: {
                    newWindow: true,
                },
            },
        },
        {
            id: "doc_download",
            category: "doc",
            name: "$itemActions:doc_download.name",
            // name: "Download",
            icon: "download",
            shouldBeVisible: [
                "isDoc",
                "hasContext:isDocDownloadAllowed",
                "isAnyOnlineStatus",
                "isAnySelection",
            ],
            commandAction: {
                name: "doc_download",
            },
        },
        {
            id: "doc_editWebDav",
            category: "doc",
            name: "$itemActions:doc_editWebDav.name",
            icon: "pencil",
            views: ["folder", "document", "docmodal", "flat", "home"],
            shouldBeVisible: [
                "isDoc",
                "hasContext:isDocEditAllowed",
                "!hasItemStatus:CloudSync",
            ],
            commandAction: {
                name: "doc_editWebDav",
            },
        },
        {
            id: "doc_editCloud",
            category: "doc",
            name: "$itemActions:doc_editCloud.name",
            icon: "pencil",
            views: ["folder", "document", "docmodal", "flat", "home"],
            shouldBeVisible: [
                "isDoc",
                "hasContext:isDocEditAllowed",
                "hasItemStatus:CloudSync",
            ],
            commandAction: {
                name: "doc_editCloud",
                props: {
                    onlineEditor: true,
                },
            },
        },
        {
            id: "doc_editCloudLocal",
            category: "doc",
            name: "$itemActions:doc_editCloudLocal.name",
            icon: "pencil",
            views: ["folder", "document", "docmodal", "flat", "home"],
            shouldBeVisible: [
                "isDoc",
                "hasContext:isDocEditAllowed",
                "hasItemStatus:CloudSync",
            ],
            commandAction: {
                name: "doc_editCloud",
                props: {
                    onlineEditor: false,
                },
            },
        },
        {
            id: "doc_delete",
            category: "doc",
            name: "$itemActions:doc_delete.name",
            icon: "trash",
            views: ["folder", "document", "docmodal", "flat"],
            shouldBeVisible: [
                "isAnyOnlineStatus",
                "isAnySelection",
                "isDoc",
                "hasContext:isDocEditAllowed",
            ],
            commandAction: { name: "doc_delete" },
        },
        {
            id: "folder_delete",
            category: "general",
            name: "$itemActions:folder_delete.name",
            icon: "trash",
            views: ["folder"],
            shouldBeVisible: [
                "isFolder",
                "hasContext:isEditAllowed",
                "licenseAllowEdit",
                "isAnyOnlineStatus",
                "isAnySelection",
            ],
            commandAction: { name: "folder_delete" },
        },
        {
            id: "folder_editClassification",
            category: "general",
            name: "$itemActions:folder_editClassification.name",
            icon: "edit",
            views: ["folder"],
            shouldBeVisible: [
                "isFolder",
                "hasContext:isEditAllowed",
                "hasContext:isFolderClassificationEditAllowed",
                "licenseAllowEdit",
                "isAnyOnlineStatus",
            ],
            commandAction: { name: "folder_editClassification" },
        },
        {
            id: "doc_previewAndEditClassification",
            category: "doc",
            name: "$itemActions:doc_editClassification.name",
            icon: "edit",
            views: ["flat", "folder", "searchresult"],
            shouldBeVisible: ["isDoc", "licenseAllowEdit", "isAnyOnlineStatus"],
            commandAction: {
                name: "doc_preview",
                props: {
                    triggerEditMode: true,
                },
            },
        },
        {
            id: "doc_editClassification",
            category: "doc",
            name: "$itemActions:doc_editClassification.name",
            icon: "edit",
            views: ["document", "docmodal"],
            shouldBeVisible: [
                "isDoc",
                "hasContext:isDocEditAllowed",
                "hasContext:isDocClassificationEditAllowed",
                "licenseAllowEdit",
                "isAnyOnlineStatus",
            ],
            commandAction: { name: "doc_editClassification" },
        },
        // {
        //     category: "doc",
        //     name: "Add to Basket",
        //     icon: "shopping-basket",
        //     shouldBeVisible: ["isDoc"]
        // },
        // {
        //     category: "doc",
        //     name: "Remove from Basket",
        //     icon: "shopping-basket",
        //     shouldBeVisible: ["isDoc"]
        // },
        {
            id: "doc_uploadVersion",
            category: "doc",
            name: "$itemActions:doc_uploadVersion.name",
            icon: "upload",
            views: ["document", "docmodal", "folder", "flat"],
            shouldBeVisible: [
                "isDoc",
                "hasContext:isDocEditAllowed",
                "licenseAllowEdit",
                "!isSyncUpload",
                "isAnyOnlineStatus",
            ],
            commandAction: { name: "doc_uploadVersion" },
        },
        {
            id: "doc_upload",
            category: "doclist",
            name: "$itemActions:doc_upload.name",
            icon: "upload",
            views: ["folder", "flat", "miniflat"],
            shouldBeVisible: [
                "isFolder",
                "hasContext:isDocAssignmentAllowed",
                "licenseAllowEdit",
                "!isSyncUpload",
                "isAnyOnlineStatus",
            ],
            // commandAction: { name: "doc_upload" }
            commandAction: {
                name: "itemFlows",
                props: {
                    direction: "in",
                    flowId: "doc_upload",
                },
            },
        },
        // {
        //     category: "general",
        //     name: "Add to Selection",
        //     icon: "check-circle-o",
        //     views: ["folder", "flat"],
        //     shouldBeVisible: ["isValidItemUri", "isDoc"]
        // },
        // {
        //     category: "general",
        //     name: "Remove from Selection",
        //     icon: "circle-o",
        //     views: ["folder", "flat"],
        //     shouldBeVisible: ["isValidItemUri", "isDoc"]
        // },
        // {
        //     category: "doc",
        //     name: "Edit",
        //     icon: "edit",
        //     shouldBeVisible: ["isDoc"]
        // },
        // {
        //     category: "doc",
        //     name: "Lock",
        //     icon: "lock",
        //     desc: "Lock file to block any changes from other users",
        //     shouldBeVisible: ["isDoc"]
        // },
        // {
        //     category: "doc",
        //     name: "Unlock",
        //     icon: "unlock",
        //     shouldBeVisible: ["isDoc"]
        // },
        {
            id: "itemFlows_input",
            category: "general",
            name: "$itemActions:itemFlows_input.name",
            icon: "plus-circle",
            views: ["folder"],
            shouldBeVisible: ["isCreateAllowed", "isAnyOnlineStatus"],
            commandAction: {
                name: "itemFlows",
                props: {
                    direction: "in",
                },
            },
        },
        // {
        //     id: "offline_depsMap",
        //     category: "offline",
        //     name: "$itemActions:offline_depsMap.name",
        //     views: ["offline_item"],
        //     shouldBeVisible: ["isAdmin", "hasFeature:offline"],
        //     commandAction: {
        //         name: "offline_depsMap",
        //     },
        // },
        {
            id: "offline_add",
            icon: "power-off",
            category: "general",
            name: "$itemActions:offline_add.name",
            shouldBeVisible: [
                "isValidItemUri",
                "!hasContext:isOffline",
                "hasFeature:offline",
                "isFolder",
                "isAnySelection",
            ],
            commandAction: {
                name: "offline_add",
            },
        },
        {
            id: "offline_del",
            icon: "power-off",
            category: "general",
            name: "$itemActions:offline_delete.name",
            shouldBeVisible: [
                "hasContext:isOffline",
                "hasFeature:offline",
                "isAnyOnlineStatus",
                "isAnySelection",
            ],
            commandAction: {
                name: "offline_delete",
            },
        },
        {
            id: "offline_syncItem",
            icon: "cloud-download",
            category: "general",
            name: "$itemActions:offline_syncItem.name",
            shouldBeVisible: ["hasContext:isOffline", "hasFeature:offline"],
            views: ["offline_item"],
            commandAction: {
                name: "offline_syncItem",
            },
        },
        {
            id: "clipboard_copyItemUri",
            category: "clipboard",
            name: "$itemActions:clipboard_copyItemUri.name",
            icon: "clipboard",
            views: ["flat", "folder", "docmodal", "mailbox", "offline_item"],
            shouldBeVisible: ["isAdmin", "isAnyOnlineStatus"],
            commandAction: {
                name: "clipboard_copyItemUri",
            },
        },
        {
            id: "clipboard_copyItemLink",
            category: "clipboard",
            name: "$itemActions:clipboard_copyItemLink.name",
            icon: "clipboard",
            views: ["flat", "folder", "docmodal", "mailbox", "offline_item"],
            shouldBeVisible: ["isAnyOnlineStatus"],
            commandAction: {
                name: "clipboard_copyItemLink",
            },
        },
        // {
        //     category: "general",
        //     name: "Copy",
        //     icon: "clone",
        //     shouldBeVisible: ["isValidItemUri"]
        // },
        // {
        //     category: "general",
        //     name: "Cut",
        //     icon: "scissors",
        //     shouldBeVisible: ["isValidItemUri"]
        // },
        // {
        //     category: "general",
        //     name: "Paste",
        //     icon: "clipboard",
        //     shouldBeVisible: ["isValidItemUri"]
        // },
        // {
        //     category: "general",
        //     name: "Paste as Link",
        //     icon: "link",
        //     shouldBeVisible: ["isValidItemUri"]
        // },
        {
            id: "wf_report",
            category: "wf",
            name: "$itemActions:wf_report.name",
            //desc: "Displays workflow report(s)",
            icon: "book",
            // views: ["document", "docmodal", "folder", "miniflat"],
            shouldBeVisible: ["isWorkflowEnabled", "hasItemStatus:WFReport"],
            commandAction: {
                name: "doc_related",
                props: {
                    itemLinkType: ItemLinkType.WorkflowReport,
                },
            },
        },
        {
            id: "wf_start",
            category: "wf",
            name: "$itemActions:wf_start.name",
            //desc: "Starts a workflow",
            icon: "check-square-o",
            views: [
                "home",
                "news",
                "document",
                "docmodal",
                "folder",
                "miniflat",
            ],
            shouldBeVisible: [
                // "hasContext:isDocEditAllowed",
                "isWorkflowEnabled",
            ],
            commandAction: {
                name: "wf_start",
            },
        },
        // {
        //     name: "Stop Workflow",
        //     desc:
        //         "Stop a WIP status",
        //     icon: "cancel",
        //     shouldBeVisible: [{ loc:"isField", name:"status", value:"WIP"}]
        // },
        {
            id: "wf_info",
            category: "wf",
            shouldBeVisible: [
                "isValidItemUri",
                // "hasContext:isDocEditAllowed",
                "isWorkflowEnabled",
            ],
            views: [
                "home",
                "folder",
                "document",
                "docmodal",
                "flat",
                "miniflat",
                "searchresult",
                "wf",
            ],
            commandAction: {
                name: "link",
                props: {
                    asModal: true,
                    docked: true,
                },
            },
            view: {
                name: "itemView_workflow",
            },
        },
        {
            id: "wf_info_noitemuri",
            category: "wf",
            shouldBeVisible: [
                "!isValidItemUri",
                // "hasContext:isDocEditAllowed",
                "isWorkflowEnabled",
            ],
            views: ["wf"],
            commandAction: {
                name: "link",
                props: {
                    asModal: true,
                    docked: true,
                },
            },
            view: {
                name: "itemView_workflow",
            },
        },
        {
            id: "ip_archive",
            category: "sync",
            name: "$itemActions:ip_archive.name",
            // desc: "Launch inPoint.Sync ArchiveDialog",
            icon: "upload",
            shouldBeVisible: [
                "isWindows",
                "isSyncEnabled",
                "isFolder",
                "hasContext:isDocAssignmentAllowed",
            ],
            commandAction: { name: "ip_archive" },
        },
        {
            id: "ip_browse",
            category: "sync",
            name: "$itemActions:ip_browse.name",
            icon: "eye",
            // desc: "Browse in inPoint.Client",
            shouldBeVisible: ["isWindows", "isSyncEnabled", "isValidItemUri"],
            commandAction: { name: "ip_browse" },
        },
        {
            id: "ip_properties",
            category: "sync",
            name: "$itemActions:ip_properties.name",
            // desc: "Launch inPoint.Sync Properties dialog",
            icon: "tasks",
            shouldBeVisible: [
                "isWindows",
                "isSyncEnabled",
                "hasContext:isDocAssignmentAllowed",
                "!hasContext:isFlatAU",
            ],
            commandAction: { name: "ip_properties" },
        },
        {
            id: "ip_edit",
            category: "sync",
            name: "$itemActions:ip_edit.name",
            // desc: "Edit (in original application) with inPoint.Sync",
            icon: "edit",
            shouldBeVisible: [
                "isWindows",
                "isSyncEnabled",
                "isDoc",
                "hasContext:isDocAssignmentAllowed",
                "hasContext:isDocEditAllowed",
                "!hasContext:isFlatAU",
            ],
            commandAction: { name: "ip_edit" },
        },
        {
            id: "ip_commandAction_itemSendAsEmail",
            category: "sync",
            name: "$itemActions:ip_commandAction.itemSendAsEmail",
            icon: "envelope-o",
            shouldBeVisible: [
                "isAnySelection",
                "isWindows",
                "isSyncEnabled",
                "isDoc",
                "hasContext:isDocSendByEmailAllowed",
            ],
            commandAction: {
                name: "ip_commandAction",
                props: {
                    commandAction: "ItemSendAsEmailCommandAction",
                },
            },
        },
        {
            id: "ip_commandAction_itemSendAsEmailLink",
            category: "sync",
            name: "$itemActions:ip_commandAction.itemSendAsEmailLink",
            icon: "envelope-o",
            shouldBeVisible: [
                "isAnySelection",
                "isWindows",
                "isSyncEnabled",
                "isValidItemUri",
            ],
            commandAction: {
                name: "ip_commandAction",
                props: {
                    commandAction: "ItemSendAsEmailLinkCommandAction",
                },
            },
        },
        // {
        //     category: "sync",
        //     name: "Make available Offline",
        //     desc: "Sync for offline use with inPoint.Sync",
        //     icon: "power-off",
        //     shouldBeVisible: ["isWindows", "isSyncEnabled"]
        // },

        // ---- Recycle Bin
        {
            id: "doc_previewRecycleBin",
            category: "recyclebin",
            name: "$itemActions:doc_preview.name",
            icon: "eye",
            views: ["flat", "folder", "searchresult", "news", "home"],
            shouldBeVisible: ["isDoc", "isRecycleBin"],
            commandAction: {
                name: "doc_preview",
            },
        },
        {
            id: "doc_clear",
            category: "recyclebin",
            name: "$itemActions:doc_clear.name",
            icon: "trash",
            shouldBeVisible: [
                "isDoc",
                "hasItemStatus:Deleted",
                "isRecycleBin",
                "isAnyOnlineStatus",
                "isAnySelection",
            ],
            commandAction: { name: "recyclebin_docClear" },
        },
        {
            id: "doc_restore",
            category: "recyclebin",
            name: "$itemActions:doc_restore.name",
            icon: "repeat",
            shouldBeVisible: [
                "isDoc",
                "hasItemStatus:Deleted",
                "!hasItemStatus:ParentDeleted",
                "isRecycleBin",
                "isAnySelection",
            ],
            commandAction: { name: "recyclebin_docRestore" },
        },
        {
            id: "folder_clear",
            category: "recyclebin",
            name: "$itemActions:folder_clear.name",
            icon: "trash",
            shouldBeVisible: [
                "isFolder",
                "hasItemStatus:Deleted",
                "isRecycleBin",
                "isAnyOnlineStatus",
                "isAnySelection",
            ],
            commandAction: { name: "recyclebin_folderClear" },
        },
        {
            id: "folder_restore",
            category: "recyclebin",
            name: "$itemActions:folder_restore.name",
            icon: "repeat",
            shouldBeVisible: [
                "isFolder",
                "hasItemStatus:Deleted",
                "!hasItemStatus:ParentDeleted",
                "isRecycleBin",
                "isAnySelection",
            ],
            commandAction: { name: "recyclebin_folderRestore" },
        },
    ],
});
